import { Grid, Paper, Tabs, Text } from '@mantine/core';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { useMyForm } from '../../../../../../react-commons/hooks/useMyForm';
import { ResourceType } from '../../../../resource/enumerations/ResourceType';
import {
    defaultSystemUserGroupResource,
    SystemUserGroupMutationResourceSchema,
    SystemUserGroupPropertyValidation,
    useSystemUserGroupActions,
} from '../../../../resource/resources/persistables/user/systemUserGroupResource';
import { PropertyInputs } from '../../../reusable-components/base/property-bases/PropertyInputs';
import { QueryBaseComponent } from '../../../../../../react-commons/query/QueryBaseComponent';
import { Form_EditableViewWrapper } from '../../../reusable-components/form-components/Form_EditableViewWrapper';
import { ResourceValidationTitleBar } from '../../../reusable-components/validation/ResourceValidationTitleBar';
import { selectReadOnly } from '../../../util/useSelectorFunctions';
import { SystemUserGroupRolesTabContent } from './SystemUserGroupRolesTabContent';
import { SystemUserGroupUsersTabContent } from './SystemUserGroupUsersTabContent';
import {
    useSystemUserGroupRoleAssignmentActions,
} from '../../../../resource/resources/persistables/user/systemUserGroupRoleAssignmentResource';

export const SystemUserGroupView= ({ id }: {
    id: string | undefined;
}) => {
    const [tabValue, setTabValue] = React.useState<string | null>('systemUserGroups');

    const readOnly = useSelector(selectReadOnly);

    const systemUserGroupActions = useSystemUserGroupActions()
    const systemUserGroupRoleAssignmentActions = useSystemUserGroupRoleAssignmentActions()

    const systemUserGroupQuery = systemUserGroupActions.useGetResource(id);
    const updateSystemUserGroup = systemUserGroupActions.useUpdateResource();
    const validateSystemUserGroup = systemUserGroupActions.useValidateResource(id);

    const roleAssignmentsQuery = systemUserGroupRoleAssignmentActions.useGetAllResources();

    const { form, formNames, onSubmit } = useMyForm({
        schema: SystemUserGroupMutationResourceSchema,
        defaultValues: defaultSystemUserGroupResource,
        submitHandler: async (data) => {
            await updateSystemUserGroup.mutateAsync({
                resource: data,
                id: data.id,
            });
        },
        reactiveValues: systemUserGroupQuery.data,
    });

    const roleAssignments = roleAssignmentsQuery.data?.filter((value) => value.systemUserGroup === id); // TODO query by systemUserGroup id instead of filtering here

    return (
        <QueryBaseComponent name="System User Group" query={systemUserGroupQuery}>
            <Form_EditableViewWrapper form={form} onSubmit={onSubmit}>
                <ResourceValidationTitleBar
                    resourceValidationResult={validateSystemUserGroup.data}
                    resourceType={ResourceType.SYSTEM_USER_GROUP}
                >
                    <Text c="dimmed">System User Group</Text>
                </ResourceValidationTitleBar>
                <Grid>
                    <Grid.Col span={12}>
                        <PropertyInputs.Name
                            name={formNames.name}
                            control={form.control}
                            readOnly={readOnly}
                            propertyValidationInformation={{
                                resourceId: id,
                                resourceType: ResourceType.SYSTEM_USER_GROUP,
                                propertyIdentifier: SystemUserGroupPropertyValidation.NAME,
                            }}
                        />
                    </Grid.Col>
                    <Grid.Col span={12}>
                        <PropertyInputs.MultilineString
                            name={formNames.description}
                            control={form.control}
                            nullable={false}
                            readOnly={readOnly}
                            propertyValidationInformation={{
                                resourceId: id,
                                resourceType: ResourceType.SYSTEM_USER_GROUP,
                                propertyIdentifier: SystemUserGroupPropertyValidation.DESCRIPTION,
                            }}
                        />
                    </Grid.Col>
                    <Grid.Col span={12}>
                        <Paper variant={'outlined'}>
                            <Tabs value={tabValue} onChange={setTabValue}>
                                <Tabs.List>
                                    <Tabs.Tab value="systemUserGroups">System User Groups</Tabs.Tab>
                                    <Tabs.Tab value="systemUserRoles">System User Roles</Tabs.Tab>
                                </Tabs.List>
                                <Tabs.Panel value={'systemUserGroups'} pt={5}>
                                    <SystemUserGroupUsersTabContent form={form} formNames={formNames} id={id} />
                                </Tabs.Panel>
                                <Tabs.Panel value={'systemUserRoles'} pt={5}>
                                    <SystemUserGroupRolesTabContent
                                        id={id}
                                        systemUserGroupRoleAssignment={roleAssignments?.find(
                                            (value) => value.systemUserGroup === id
                                        )}
                                    />
                                </Tabs.Panel>
                            </Tabs>
                        </Paper>
                    </Grid.Col>
                </Grid>
            </Form_EditableViewWrapper>
        </QueryBaseComponent>
    );
};


